import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Newspaper, ChevronLeft, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { AdSlot } from "@/components/AdSlot";
import { ReelCard, Archives, Attribution, StaleBanner, yearLabel } from "@/components/OnThisDayReel";
import { DateIntro } from "@/components/DateIntro";

const MONTHS = ["january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december"];
const TABS = [["events", "Events"], ["births", "Births"]];

const setMeta = (name, content) => {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
  el.setAttribute("content", content);
};

// Accepts /on-this-day, /on-this-day/october-11, /history/october-11 and /history/10/11
const resolveSlug = ({ slug, month, day }) => {
  if (month && day) return `${MONTHS[parseInt(month, 10) - 1] || "x"}-${parseInt(day, 10)}`;
  if (slug) return slug.toLowerCase();
  const now = new Date();
  return `${MONTHS[now.getUTCMonth()]}-${now.getUTCDate()}`;
};

export default function HistoryDay() {
  const params = useParams();
  const slug = resolveSlug(params);
  const [day, setDay] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("events");

  useEffect(() => {
    setDay(null); setError(""); setTab("events");
    api.get(`/onthisday/history/${slug}`).then((r) => setDay(r.data)).catch((e) => setError(e.response?.status === 404 ? "That day doesn't exist." : "The history feed is unavailable right now."));
  }, [slug]);

  useEffect(() => {
    if (!day) return;
    document.title = day.seo.title;
    setMeta("description", day.seo.description);
    let canon = document.querySelector('link[rel="canonical"]');
    if (!canon) { canon = document.createElement("link"); canon.setAttribute("rel", "canonical"); document.head.appendChild(canon); }
    canon.setAttribute("href", `https://oddlytrue.co/history/${day.slug}`);
    const faq = { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: [{ "@type": "Question", name: `What happened on ${day.label}?`,
      acceptedAnswer: { "@type": "Answer", text: day.picks.map((p) => `${yearLabel(p.year)}: ${p.text}`).join(" ") } }] };
    const list = { "@context": "https://schema.org", "@type": "ItemList", name: day.seo.h1, dateModified: day.fetched_at, license: "https://creativecommons.org/licenses/by-sa/4.0/",
      itemListElement: day.picks.map((p, i) => ({ "@type": "ListItem", position: i + 1, name: `${yearLabel(p.year)}: ${p.text}`, url: p.url || p.page?.url })) };
    document.querySelectorAll("script[data-history-ld]").forEach((el) => el.remove());
    [faq, list].forEach((obj) => { const ld = document.createElement("script"); ld.type = "application/ld+json"; ld.setAttribute("data-history-ld", "1"); ld.text = JSON.stringify(obj); document.head.appendChild(ld); });
    return () => { document.querySelectorAll("script[data-history-ld]").forEach((el) => el.remove()); };
  }, [day]);

  const list = day ? day[tab] || [] : [];

  return (
    <div className="min-h-screen bg-paper-light">
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10" data-testid="history-day-page">
        <div className="border-y-4 border-double border-ink py-4 text-center">
          <div className="flex items-center justify-center gap-2">
            <Newspaper className="w-4 h-4 text-gold" />
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold">Vintage News · Odd history for {day ? day.label : "today"}</p>
          </div>
          <h1 data-testid="history-title" className="mt-2 font-vintage text-4xl sm:text-5xl font-bold text-ink leading-tight tracking-tight">{day ? day.seo.h1 : `What Happened On ${slug.replace(/^(\w)/, (c) => c.toUpperCase()).replace("-", " ")}`}</h1>
          {day && <p className="mt-3 text-base text-ink-muted">{day.counts.events} events and {day.counts.births} births, refreshed daily. <Link to={`/?date=${day.date.slice(3, 5)}-${day.date.slice(0, 2)}`} data-testid="history-oddlytrue-link" className="font-semibold text-gold hover:underline">OddlyTrue's own archive for {day.label_uk} →</Link></p>}
        </div>

        {day && (
          <div className="mt-5 flex items-center justify-between gap-2">
            <Link to={`/history/${day.prev_slug}`} data-testid="history-prev" className="inline-flex items-center gap-1 px-4 h-10 border border-ink bg-[#fbf8f1] text-sm font-semibold text-ink hover:bg-ink hover:text-white transition-colors"><ChevronLeft className="w-4 h-4" /> {day.prev_slug.replace("-", " ").replace(/^\w/, (c) => c.toUpperCase())}</Link>
            <Link to={`/history/${day.next_slug}`} data-testid="history-next" className="inline-flex items-center gap-1 px-4 h-10 border border-ink bg-[#fbf8f1] text-sm font-semibold text-ink hover:bg-ink hover:text-white transition-colors">{day.next_slug.replace("-", " ").replace(/^\w/, (c) => c.toUpperCase())} <ChevronRight className="w-4 h-4" /></Link>
          </div>
        )}

        {error && <p data-testid="history-error" className="mt-8 text-sm text-ink-muted">{error} <Link to="/" className="font-semibold text-gold hover:underline">Back to today →</Link></p>}

        {day && (
          <>
            <DateIntro ddmm={`${day.date.slice(3, 5)}-${day.date.slice(0, 2)}`} intro={day.intro} className="mt-8" />
            <section className="mt-8">
              <StaleBanner day={day} />
              <h2 className="font-vintage text-xl font-bold text-ink mb-3 border-b-2 border-ink pb-1">{day.picks.length} odd things that happened on {day.label}</h2>
              <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="history-picks">{day.picks.map((it, i) => <ReelCard key={`${it.year}-${i}`} it={it} testid={`history-pick-${i + 1}`} />)}</ul>
            </section>

            {day.holidays?.length > 0 && (
              <section className="mt-8" data-testid="history-holidays">
                <h2 className="font-vintage text-xl font-bold text-ink mb-3 border-b-2 border-ink pb-1">Observed today</h2>
                <ul className="flex flex-wrap gap-2">{day.holidays.map((h, i) => <li key={i} className="px-3 py-1.5 bg-[#fbf8f1] border border-ink/30 text-[13px] text-ink">{h.page?.url ? <a href={h.page.url} target="_blank" rel="noopener noreferrer" className="hover:underline">{h.text}</a> : h.text}</li>)}</ul>
              </section>
            )}

            <AdSlot id="adsense-history" slot="MID" size="rect300" className="mt-8" />

            <section className="mt-8">
              <div className="flex flex-wrap gap-2 mb-4">
                {TABS.map(([k, label]) => (
                  <button key={k} type="button" data-testid={`history-tab-${k}`} onClick={() => setTab(k)} className={`px-4 h-10 text-sm font-semibold border transition-colors ${tab === k ? "bg-ink text-white border-ink" : "bg-[#fbf8f1] text-ink border-ink/30 hover:border-ink"}`}>{label} <span className="text-[11px] opacity-70">{(day[k] || []).length}</span></button>
                ))}
              </div>
              <ul className="divide-y divide-ink/10 border-y border-ink/20" data-testid={`history-list-${tab}`}>
                {list.map((it, i) => (
                  <li key={`${it.year}-${i}`} className="flex gap-3 px-2 py-3">
                    <span className="w-14 shrink-0 font-vintage text-[16px] font-bold text-ink">{yearLabel(it.year)}</span>
                    <span className="min-w-0 flex-1 text-[14px] text-ink leading-relaxed">{it.text}{it.page?.url && <> <a href={it.page.url} target="_blank" rel="noopener noreferrer" className="text-[12px] font-semibold text-gold hover:underline whitespace-nowrap">Wikipedia →</a></>}</span>
                  </li>
                ))}
              </ul>
            </section>

            <Archives day={day} testid="history-archives" />
            <Attribution day={day} />
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
